/** Движок чтения: загрузка книги, позиция, настройки, навигация по главам.
 *
 * Вынесен из страницы, потому что читалок в проекте две — обычная и языковая.
 * Различаются они оформлением и тем, что языковая накладывает поверх текста
 * свой слой, а вся механика чтения у них одна и та же: разойдись она, позиция
 * в одной и той же книге стала бы означать разное.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { api } from '../api'
import type { Chapter, Manifest, ReadingState } from '../api'
import { PositionPusher, progressAt } from './position'
import {
  applySettings,
  loadSettings,
  saveSettings,
  type ReaderSettings,
} from './settings'

// Насколько дальше должно быть прочитано на другом устройстве, чтобы
// предлагать переход. Меньший разрыв — обычный шум от округления.
const JUMP_THRESHOLD = 0.01

export interface Reader {
  manifest: Manifest | null
  chapter: Chapter | null
  chapterIndex: number
  currentBlock: number
  progress: number
  isLast: boolean
  error: string | null

  settings: ReaderSettings
  updateSettings: (patch: Partial<ReaderSettings>) => void

  /** Прочитанное дальше на другом устройстве — предложение перейти. */
  elsewhere: ReadingState | null
  dismissElsewhere: () => void

  /** Панель прячется при прокрутке вниз, возвращается при движении вверх. */
  barHidden: boolean

  goToChapter: (index: number, block?: number) => void
  /** Ссылка на элемент блока: по ним считается видимая позиция. */
  registerBlock: (index: number, element: HTMLElement | null) => void
}

export function useReader(bookId: number): Reader {
  const [manifest, setManifest] = useState<Manifest | null>(null)
  const [chapter, setChapter] = useState<Chapter | null>(null)
  const [chapterIndex, setChapterIndex] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const [settings, setSettings] = useState<ReaderSettings>(loadSettings)
  const [barHidden, setBarHidden] = useState(false)
  const [currentBlock, setCurrentBlock] = useState(0)
  const [elsewhere, setElsewhere] = useState<ReadingState | null>(null)

  const pusher = useMemo(() => new PositionPusher(bookId), [bookId])
  const blockRefs = useRef<Map<number, HTMLElement>>(new Map())
  // Блок, к которому надо прокрутить сразу после отрисовки главы.
  const pendingScroll = useRef<number | null>(null)
  const lastScrollY = useRef(0)

  useEffect(() => applySettings(settings), [settings])

  // ---- начальная загрузка: манифест + сохранённая позиция ----
  useEffect(() => {
    let cancelled = false
    async function load() {
      try {
        const [loaded, book] = await Promise.all([api.manifest(bookId), api.book(bookId)])
        if (cancelled) return
        setManifest(loaded)

        const state = book.reading
        const startChapter = state?.chapter ?? 0
        pendingScroll.current = state?.block ?? 0
        setChapterIndex(Math.min(startChapter, Math.max(0, loaded.chapters.length - 1)))

        // Если на другом устройстве прочитано заметно дальше — предложим перейти,
        // но не перенесём молча: пользователь мог намеренно вернуться назад.
        if (
          state &&
          state.max_progress > state.progress + JUMP_THRESHOLD &&
          state.max_chapter !== undefined
        ) {
          setElsewhere(state)
        }
      } catch (exc) {
        if (!cancelled) setError(describe(exc))
      }
    }
    void load()
    return () => {
      cancelled = true
    }
  }, [bookId])

  // ---- загрузка текущей главы ----
  useEffect(() => {
    if (!manifest) return
    let cancelled = false
    setChapter(null)
    api
      .chapter(bookId, chapterIndex)
      .then((loaded) => {
        if (cancelled) return
        blockRefs.current.clear()
        setChapter(loaded)
      })
      .catch((exc) => {
        if (!cancelled) setError(describe(exc))
      })
    return () => {
      cancelled = true
    }
  }, [bookId, chapterIndex, manifest])

  // ---- прокрутка к нужному блоку после отрисовки ----
  useEffect(() => {
    if (!chapter) return
    const target = pendingScroll.current
    pendingScroll.current = null
    if (target === null) {
      window.scrollTo({ top: 0 })
      return
    }
    // Эффект выполняется уже после обновления DOM, поэтому элементы главы
    // существуют и имеют размеры — ждать кадра не нужно.
    const element = blockRefs.current.get(target)
    if (element) element.scrollIntoView({ block: 'start' })
    else window.scrollTo({ top: 0 })
  }, [chapter])

  // ---- отслеживание видимого блока ----
  useEffect(() => {
    if (!chapter || !manifest) return
    let frame = 0

    const measure = () => {
      const block = topmostVisibleBlock(blockRefs.current)
      if (block === null) return
      setCurrentBlock(block)
      pusher.update(chapterIndex, block, progressAt(manifest, chapterIndex, block))
    }

    const onScroll = () => {
      const y = window.scrollY
      setBarHidden(y > 120 && y > lastScrollY.current)
      lastScrollY.current = y

      if (frame) return
      frame = requestAnimationFrame(() => {
        frame = 0
        measure()
      })
    }

    window.addEventListener('scroll', onScroll, { passive: true })
    // Первое измерение — сразу, а не через кадр: в фоновой вкладке кадры не
    // отрисовываются, и позиция открытой главы иначе никогда бы не сохранилась.
    measure()
    return () => {
      window.removeEventListener('scroll', onScroll)
      if (frame) cancelAnimationFrame(frame)
    }
  }, [chapter, chapterIndex, manifest, pusher])

  // ---- отправка позиции при уходе ----
  useEffect(() => {
    const flush = () => pusher.flush()
    const onHidden = () => {
      if (document.visibilityState === 'hidden') flush()
    }
    window.addEventListener('pagehide', flush)
    document.addEventListener('visibilitychange', onHidden)
    return () => {
      window.removeEventListener('pagehide', flush)
      document.removeEventListener('visibilitychange', onHidden)
      flush()
    }
  }, [pusher])

  const goToChapter = useCallback((index: number, block = 0) => {
    pendingScroll.current = block
    setChapterIndex(index)
  }, [])

  const registerBlock = useCallback((index: number, element: HTMLElement | null) => {
    if (element) blockRefs.current.set(index, element)
    else blockRefs.current.delete(index)
  }, [])

  const updateSettings = useCallback(
    (patch: Partial<ReaderSettings>) => {
      setSettings((prev) => {
        const next = { ...prev, ...patch }
        saveSettings(next)
        return next
      })
    },
    [],
  )

  return {
    manifest,
    chapter,
    chapterIndex,
    currentBlock,
    progress: manifest ? progressAt(manifest, chapterIndex, currentBlock) : 0,
    isLast: manifest ? chapterIndex >= manifest.chapters.length - 1 : false,
    error,
    settings,
    updateSettings,
    elsewhere,
    dismissElsewhere: useCallback(() => setElsewhere(null), []),
    barHidden,
    goToChapter,
    registerBlock,
  }
}

/** Переключение глав с клавиатуры. Живёт отдельно: в языковой читалке стрелки
 *  заняты выбором слова, и подключать их там не нужно. */
export function useChapterKeys(reader: Reader, onEscape?: () => void): void {
  const { manifest, chapterIndex, goToChapter } = reader
  useEffect(() => {
    if (!manifest) return
    const onKey = (event: KeyboardEvent) => {
      if (event.target instanceof HTMLInputElement) return
      if (event.key === 'ArrowRight' || event.key === 'PageDown') {
        if (chapterIndex < manifest.chapters.length - 1) goToChapter(chapterIndex + 1)
      } else if (event.key === 'ArrowLeft' || event.key === 'PageUp') {
        if (chapterIndex > 0) goToChapter(chapterIndex - 1)
      } else if (event.key === 'Escape') {
        onEscape?.()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [chapterIndex, goToChapter, manifest, onEscape])
}

/** Верхний блок, который ещё виден на экране. */
function topmostVisibleBlock(refs: Map<number, HTMLElement>): number | null {
  let best: number | null = null
  let bestTop = Number.NEGATIVE_INFINITY
  for (const [index, element] of refs) {
    const rect = element.getBoundingClientRect()
    // Ищем последний блок, начало которого ещё не ушло за верх экрана.
    if (rect.top <= 80 && rect.top > bestTop) {
      bestTop = rect.top
      best = index
    }
  }
  if (best !== null) return best
  // Ничего не прокручено — считаем текущим самый первый блок.
  let first: number | null = null
  for (const index of refs.keys()) {
    if (first === null || index < first) first = index
  }
  return first
}

export function describe(error: unknown): string {
  if (error && typeof error === 'object' && 'message' in error) {
    return String((error as { message: unknown }).message)
  }
  return 'Не удалось загрузить книгу'
}
